<template>
  <div class="plc-root">
    <!-- Loading state (autonomous mode) -->
    <div v-if="isAutonomous && loading" class="plc-loading">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <template v-else>
      <!-- Legend -->
      <div v-if="showLegend" class="chart-legend-top">
        <div
          v-for="item in legendItems"
          :key="item.index"
          class="legend-item"
          :class="{ 'legend-item--hidden': item.hidden }"
          @click="toggleDataset(item.index)"
        >
          <span
            class="legend-line"
            :style="{
              borderTopColor: item.color,
              borderTopStyle: item.dashed ? 'dashed' : 'solid',
            }"
          />
          <span class="legend-label">{{ item.label }}</span>
        </div>
      </div>

      <!-- Chart -->
      <div class="chart-wrapper" ref="chartWrapper">
        <div
					v-if="showOverlay"
          ref="overlayEl"
          class="chart-overlay"
          :class="{ 'overlay-hidden': !overlayVisible }"
          :style="overlayStyle"
          @pointerdown.stop="onOverlayPointerDown"
        >
          <v-btn
            small
            icon
            class="overlay-close-btn"
            @click="closeOverlay">
            <v-icon small>{{ showOverlay ? 'mdi-close' : 'mdi-information-outline' }}</v-icon>
          </v-btn>
          <div class="overlay-top">Último Control: {{ (overlayData && overlayData.date) ? formatDate(overlayData.date) : '—' }}</div>
          <div class="overlay-compact">
            <div class="ov-item">
              <div class="ov-key">Económico</div>
              <div class="ov-val">Plan: {{ formatPct(overlayData && overlayData.ec ? overlayData.ec.planned : null) }}</div>
              <div class="ov-val">Real: {{ formatPct(overlayData && overlayData.ec ? overlayData.ec.real : null) }}</div>
              <div class="ov-val" :class="deltaClass(overlayData && overlayData.ec ? overlayData.ec.delta : null)">{{ formatDelta(overlayData && overlayData.ec ? overlayData.ec.delta : null) }}</div>
            </div>
            <div class="ov-item">
              <div class="ov-key">Físico</div>
              <div class="ov-val">Plan {{ formatPct(overlayData && overlayData.ph ? overlayData.ph.planned : null) }}</div>
              <div class="ov-val">Real: {{ formatPct(overlayData && overlayData.ph ? overlayData.ph.real : null) }}</div>
              <div class="ov-val" :class="deltaClass(overlayData && overlayData.ph ? overlayData.ph.delta : null)">{{ formatDelta(overlayData && overlayData.ph ? overlayData.ph.delta : null) }}</div>
            </div>
          </div>
        </div>

        <!-- Toggle button shown when overlay is hidden -->
        <v-btn
          v-if="!overlayVisible && showOverlay"
          small
          icon
          class="overlay-toggle-btn"
          :style="overlayToggleStyle"
          @click.prevent="openOverlay"
        >
          <v-icon small>mdi-information-outline</v-icon>
        </v-btn>

        <LineChart ref="chartRef" :chartdata="chartData" :options="resolvedOptions" :height="chartHeight" :show-data-labels="showDataLabels" />
      </div>

      <!-- Cut-off label -->
      <!-- <div class="chart-label-bottom">
        Corte {{ resolvedRows.length ? formatDate(resolvedRows[resolvedRows.length - 1].date) : '' }}
      </div> -->
    </template>
  </div>
</template>

<script>
import LineChart from '@/components/Charts/Line'
import { getGeneralProjectPlanning } from '@/helpers/api/planning'

const REPROG_COLORS = ['#D97706', '#EA4C4C', '#8B5CF6', '#06B6D4', '#F59E0B']

const DEFAULT_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  legend: { display: false },
  tooltips: {
    mode: 'index',
    intersect: false,
    callbacks: {
      label: (item, data) => {
        const label = data.datasets[item.datasetIndex].label
        const val = item.yLabel
        return `${label}: ${val !== null && val !== undefined ? parseFloat(val).toFixed(1) + '%' : '—'}`
      },
    },
  },
  scales: {
    xAxes: [{ gridLines: { color: 'rgba(0,0,0,0.05)' }, ticks: { fontSize: 10, fontColor: '#6B7280', autoSkip: true, maxTicksLimit: 12, maxRotation: 45 } }],
    yAxes: [{
      ticks: { min: 0, max: 100, stepSize: 25, fontSize: 11, fontColor: '#6B7280', callback: v => v + '%' },
      gridLines: { color: 'rgba(0,0,0,0.05)' },
    }],
  },
}

export default {
  name: 'PlanningLineChart',
  components: { LineChart },

  props: {
    rows:             { type: Array,   default: null },
    reprogramLabels:  { type: Array,   default: null },
    reprogColors:     { type: Array,   default: () => [...REPROG_COLORS] },
    options:          { type: Object,  default: null },
    showLegend:       { type: Boolean, default: true },
    showAllPoints:     { type: Boolean, default: true },
    series:           { type: Array,   default: null },
    chartHeight:      { type: Number,  default: 280 },
    showOverlay:       { type: Boolean, default: true },
    showDataLabels:    { type: Boolean, default: false },
    effectivePlanEc:  { type: [Number, String], default: null },
    effectivePlanPh:  { type: [Number, String], default: null },
  },

  data() {
    return {
      hiddenDatasets: new Set(),
      internalRows: [],
      internalReprogramLabels: [],
      loading: false,
      // Overlay local state
      overlayVisible: true,
      overlayPosition: { left: null, top: null }, // null = use default css bottom/right
      isDraggingOverlay: false,
      dragStart: null,
      overlayInitialPos: null,
    }
  },

  computed: {
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },

    isAutonomous() {
      return this.rows === null
    },

    resolvedRows() {
      return this.isAutonomous ? this.internalRows : this.rows
    },

    resolvedReprogramLabels() {
      if (this.isAutonomous) return this.internalReprogramLabels
      if (this.reprogramLabels) return this.reprogramLabels
      const maxReprogs = Math.max(0, ...this.rows.map(r => ((r.reprograms || r.reprogram || []).length)))
      return Array.from({ length: maxReprogs }, (_, i) => `Reprog. ${i + 1}`)
    },

    sampledIndices() {
      const len = (this.resolvedRows && this.resolvedRows.length) || 0
      const POINTS_SAMPLING_THRESHOLD = 100
      const MAX_VISIBLE_POINTS = 40
      if (this.showAllPoints || len <= POINTS_SAMPLING_THRESHOLD) return null

      const maxVisible = Math.min(MAX_VISIBLE_POINTS, len)
      const indices = new Set()
      for (let i = 0; i < maxVisible; i++) {
        const idx = Math.round(i * (len - 1) / (maxVisible - 1))
        indices.add(idx)
      }
      return indices
    },

    resolvedOptions() {
			const base = this.options ? Object.assign({}, this.options) : Object.assign({}, DEFAULT_OPTIONS)

			base.scales = base.scales ? Object.assign({}, base.scales) : {}
			const x0 = (base.scales.xAxes && base.scales.xAxes[0]) ? Object.assign({}, base.scales.xAxes[0]) : { ticks: {} }
			x0.ticks = Object.assign({ autoSkip: true, maxTicksLimit: 12, maxRotation: 45 }, x0.ticks || {})

			const totalRows = (this.resolvedRows && this.resolvedRows.length) || 0
			const sampleSet = this.sampledIndices
			const showAll = !!this.showAllPoints

			const MAX_X_LABELS_SHOW_ALL = 25
			const MAX_X_LABELS_SAMPLED  = 20

			let visibleIndices

			if (!showAll && sampleSet) {
				const sampleArray = Array.from(sampleSet).sort((a, b) => a - b)
				const step = sampleArray.length > MAX_X_LABELS_SAMPLED
					? Math.ceil(sampleArray.length / MAX_X_LABELS_SAMPLED)
					: 1
				visibleIndices = sampleArray.filter((_, pos) => pos % step === 0)
			} else {
				const step = totalRows > MAX_X_LABELS_SHOW_ALL
					? Math.ceil(totalRows / MAX_X_LABELS_SHOW_ALL)
					: 1
				visibleIndices = Array.from({ length: totalRows }, (_, i) => i).filter((_, pos) => pos % step === 0)
			}

			const visibleSet = new Set(visibleIndices)

			x0.ticks.autoSkip = false
			x0.ticks.maxRotation = 45
			x0.ticks.padding = 4
			x0.ticks.callback = (value, index) => {
				if (!visibleSet.has(index)) return ''
				const row = this.resolvedRows && this.resolvedRows[index]
				if (!row || !row.date) return ''
				const [y, m, d] = row.date.substring(0, 10).split('-')
				const short = `${d}/${m}`

				const prevVisibleIdx = visibleIndices[visibleIndices.indexOf(index) - 1]
				const prevYear = prevVisibleIdx !== undefined
					? this.resolvedRows[prevVisibleIdx]?.date?.substring(0, 4)
					: null
				const showYear = index === visibleIndices[0] || (prevYear && prevYear !== y)

				return showYear ? `${short}/${y}` : short
			}

			base.layout = Object.assign({}, base.layout || {}, { padding: { bottom: 20 } })
			base.scales.xAxes = [x0]

			const GLOBAL_HIDE_POINTS_THRESHOLD = 5000
			if (totalRows > GLOBAL_HIDE_POINTS_THRESHOLD) {
				base.elements = Object.assign({}, base.elements || {})
				base.elements.point = Object.assign({}, base.elements.point || {}, { radius: 0 })
			}

			if (totalRows > 1500) {
				base.animation = Object.assign({}, base.animation || {}, { duration: 0 })
			}

			const plugins = base.plugins || {}
			const datalabels = plugins.datalabels || {}
			base.plugins = {
				...plugins,
				datalabels: {
					...datalabels,
					display: this.showDataLabels
				}
			}

			return base
		},

    activeSeries() {
      return this.series
        ? new Set(this.series)
        : new Set(['planEc', 'planPh', 'realEc', 'realPh', 'reprog'])
    },

    chartData() {
      const labels = this.resolvedRows.map(r => this.formatDate(r.date))
      const colors = this.reprogColors || REPROG_COLORS

      const baseDefs = [
        { key: 'planEc', label: 'Plan Ec.',  field: 'ec_programmed_progress', borderColor: '#2D4B95', pointRadius: 2, spanGaps: true  },
        { key: 'planPh', label: 'Plan Fís.', field: 'ph_programmed_progress', borderColor: '#0EA5E9', pointRadius: 2, spanGaps: true  },
        { key: 'realEc', label: 'Real Ec.',  field: 'ec_actual_progress',     borderColor: '#16A34A', pointRadius: 2, spanGaps: false },
        { key: 'realPh', label: 'Real Fís.', field: 'ph_actual_progress',     borderColor: '#22C55E', pointRadius: 2, spanGaps: false },
      ]

      const base = baseDefs
        .filter(s => this.activeSeries.has(s.key))
        .map(s => ({
          label: s.label,
          data: this.resolvedRows.map(r => this.toChartVal(r[s.field])),
          borderColor: s.borderColor,
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: s.pointRadius,
          pointBackgroundColor: s.borderColor,
          fill: false,
          lineTension: 0.3,
          spanGaps: s.spanGaps,
        }))

      const reprog = this.activeSeries.has('reprog')
        ? this.resolvedReprogramLabels.flatMap((label, idx) => {
            const color = colors[idx % colors.length]
            return [
              {
                label: `${label} Ec.`,
                data: this.resolvedRows.map(r => {
                  const reprograms = r.reprogram || r.reprograms || []
                  const rep = reprograms[idx]
                  return rep ? this.toChartVal(rep.ec_progress) : null
                }),
                borderColor: color, backgroundColor: 'transparent',
                borderWidth: 2, borderDash: [6, 4],
                pointRadius: 3, pointBackgroundColor: color,
                fill: false, lineTension: 0.3, spanGaps: true,
              },
              {
                label: `${label} Fís.`,
                data: this.resolvedRows.map(r => {
                  const reprograms = r.reprogram || r.reprograms || []
                  const rep = reprograms[idx]
                  return rep ? this.toChartVal(rep.ph_progress) : null
                }),
                borderColor: color, backgroundColor: 'transparent',
                borderWidth: 1.5, borderDash: [3, 3],
                pointRadius: 2, pointBackgroundColor: color,
                fill: false, lineTension: 0.3, spanGaps: true,
              },
            ]
          })
        : []

      const datasets = [...base, ...reprog]
      const sampleSet = this.sampledIndices
      if (sampleSet && sampleSet.size) {
        for (let i = 0; i < datasets.length; i++) {
          const ds = datasets[i]
          const origRadius = Number.isFinite(ds.pointRadius) ? ds.pointRadius : 3

          ds.pointRadius = labels.map((_, idx) => sampleSet.has(idx) ? origRadius : 0)

          const origPointColor = ds.pointBackgroundColor || ds.borderColor || 'transparent'
          ds.pointBackgroundColor = labels.map((_, idx) => sampleSet.has(idx) ? origPointColor : 'transparent')

          ds.borderWidth = ds.borderWidth ? Math.max(1, ds.borderWidth) : 1
        }
      }

      return { labels, datasets }
    },

    overlayData() {
      const rows = this.resolvedRows || []
			console.log('OverlayData computed, rows length', rows)
      if (!rows.length) return null

      // find last index with any actual progress
      let lastIdx = -1
      for (let i = rows.length - 1; i >= 0; i--) {
        const r = rows[i]
        if ((r.ec_actual_progress !== null && r.ec_actual_progress !== undefined && r.ec_actual_progress !== '') ||
            (r.ph_actual_progress !== null && r.ph_actual_progress !== undefined && r.ph_actual_progress !== '')) {
          lastIdx = i
          break
        }
      }
      if (lastIdx === -1) return null

      const lastRow = rows[lastIdx]
      const planningRows = rows
        .filter(row => row && row.date && row.date <= this.todayISO())
        .slice()
        .sort((a, b) => a.date.localeCompare(b.date))

      const findLast = (sourceRows, field) => {
        for (let i = sourceRows.length - 1; i >= 0; i--) {
          const v = sourceRows[i][field]
          if (v !== null && v !== undefined && v !== '') return Number(v)
        }
        return null
      }

      const lastPlannedEc = findLast(planningRows, 'ec_programmed_progress')
      const ecPlanned = this.effectivePlanEc !== null
        ? Number(this.effectivePlanEc)
        : this.getEffectivePlan(planningRows, 'ec_progress', lastPlannedEc)
      const ecReal = findLast(rows.slice(0, lastIdx + 1), 'ec_actual_progress')
      const ecDelta = (ecReal !== null && ecPlanned !== null) ? Number((ecReal - ecPlanned).toFixed(1)) : null

      // PH planned / real
      const lastPlannedPh = findLast(planningRows, 'ph_programmed_progress')
      const phPlanned = this.effectivePlanPh !== null
        ? Number(this.effectivePlanPh)
        : this.getEffectivePlan(planningRows, 'ph_progress', lastPlannedPh)
      const phReal = findLast(rows.slice(0, lastIdx + 1), 'ph_actual_progress')
      const phDelta = (phReal !== null && phPlanned !== null) ? Number((phReal - phPlanned).toFixed(1)) : null

      return {
        date: lastRow.date || null,
        ec: { planned: ecPlanned, real: ecReal, delta: ecDelta },
        ph: { planned: phPlanned, real: phReal, delta: phDelta },
      }
    },

    legendItems() {
      return this.chartData.datasets.map((ds, idx) => ({
        index: idx,
        label: ds.label,
        color: ds.borderColor,
        dashed: !!(ds.borderDash && ds.borderDash.length),
        hidden: this.hiddenDatasets.has(idx),
      }))
    },

    overlayStyle() {
      const style = {}
      if (this.overlayPosition && this.overlayPosition.left !== null && this.overlayPosition.top !== null) {
        style.left = this.overlayPosition.left + 'px'
        style.top = this.overlayPosition.top + 'px'
        style.right = 'auto'
        style.bottom = 'auto'
      }
      return style
    },
    overlayToggleStyle() {
      // depend on overlayPosition and overlayVisible so this computed updates when overlay moves or is toggled
      // const _posLeft = this.overlayPosition && this.overlayPosition.left
      // const _posTop = this.overlayPosition && this.overlayPosition.top
      // const _vis = this.overlayVisible
      try {
        const wrapper = this.$refs.chartWrapper
        const overlayEl = this.$refs.overlayEl
        const fallback = { top: '4px', right: '4px' }
        if (!wrapper || !overlayEl) return fallback

        const toggleApprox = 28
        // Prefer using the explicit overlayPosition (relative to wrapper) when available
        if (this.overlayPosition && typeof this.overlayPosition.left === 'number' && typeof this.overlayPosition.top === 'number') {
          const overlayWidth = overlayEl.offsetWidth || overlayEl.getBoundingClientRect().width || 120
          const left = this.overlayPosition.left + Math.max(0, overlayWidth - toggleApprox)
          const top = this.overlayPosition.top + 4
          return { left: Math.round(left) + 'px', top: Math.round(top) + 'px', right: 'auto' }
        }

        // Fallback: measure DOM rects
        const wrapperRect = wrapper.getBoundingClientRect()
        const overlayRect = overlayEl.getBoundingClientRect()
        if (!overlayRect.width || !overlayRect.height) return fallback
        const left = overlayRect.left - wrapperRect.left + Math.max(0, overlayRect.width - toggleApprox)
        const top = overlayRect.top - wrapperRect.top + 4
        return { left: Math.round(left) + 'px', top: Math.round(top) + 'px', right: 'auto' }
      } catch (e) {
        return { top: '4px', right: '4px' }
      }
    },
  },

  watch: {
    selectedProject(newProj, oldProj) {
      if (this.isAutonomous && newProj && newProj.id !== oldProj?.id) {
        this.internalRows = []
        this.internalReprogramLabels = []
        this.fetchData()
      }
      // cargar posición guardada al cambiar de proyecto
      this.loadOverlayPosition && this.loadOverlayPosition()
    },
  },

  mounted() {
    if (this.isAutonomous && this.selectedProject) {
      this.fetchData()
    }
    this.loadOverlayPosition && this.loadOverlayPosition()
		console.log('ActiveSeries on mount', this.activeSeries)
  },

  beforeDestroy() {
    // cleanup any global listeners
    try {
      document.removeEventListener('pointermove', this.onOverlayPointerMove)
      document.removeEventListener('pointerup', this.onOverlayPointerUp)
    } catch (e) {
			console.log('Error removing overlay listeners', e)
		}
  },

  methods: {
    todayISO() {
      const d = new Date()
      return d.toISOString().split('T')[0]
    },

    getEffectivePlan(rows, field, base) {
      const candidates = []
      if (base !== null && !isNaN(base)) candidates.push(base)

      const reprogramCount = this.resolvedReprogramLabels.length
      for (let index = 0; index < reprogramCount; index++) {
        for (let rowIndex = rows.length - 1; rowIndex >= 0; rowIndex--) {
          const reprograms = rows[rowIndex].reprogram || rows[rowIndex].reprograms || []
          const value = reprograms[index] && reprograms[index][field]
          if (value !== null && value !== undefined && value !== '') {
            const numericValue = Number(value)
            if (!isNaN(numericValue)) candidates.push(numericValue)
            break
          }
        }
      }

      return candidates.length ? Math.max(...candidates) : null
    },

    async fetchData() {
      this.loading = true
      try {
        const data = await getGeneralProjectPlanning({ project_id: this.selectedProject.id })
        if (data && data.progresses && data.progresses.length) {
          const progresses = data.progresses
          const toDate = (d) => d ? d.substring(0, 10) : ''

          const maxReprogs = Math.max(...progresses.map(p => (p.reprograms || []).length))
          this.internalReprogramLabels = Array.from({ length: maxReprogs }, (_, i) => `Reprog. ${i + 1}`)

          this.internalRows = progresses.map(p => ({
            _id: Math.random(),
            id: p.id,
            date: toDate(p.date),
            ec_programmed_progress: p.ec_programmed_progress,
            ph_programmed_progress: p.ph_programmed_progress,
            ec_actual_progress: p.ec_actual_progress,
            ph_actual_progress: p.ph_actual_progress,
            reprogram: Array.from({ length: maxReprogs }, (_, idx) => {
              const rep = p.reprograms && p.reprograms[idx]
              return {
                id: rep ? rep.id : null,
                ec_progress: rep ? rep.ec_progress : null,
                ph_progress: rep ? rep.ph_progress : null,
              }
            }),
          }))
        }
      } finally {
        this.loading = false
      }
    },

    toggleDataset(index) {
      const chart = this.$refs.chartRef?.$data._chart
      if (!chart) return
      const meta = chart.getDatasetMeta(index)
      meta.hidden = !meta.hidden
      chart.update()
      const next = new Set(this.hiddenDatasets)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      this.hiddenDatasets = next
    },

    formatDate(isoDate) {
      if (!isoDate) return ''
      const [y, m, d] = isoDate.substring(0, 10).split('-')
      return `${d}/${m}/${y}`
    },

    toChartVal(v) {
      return v === null || v === undefined || v === '' || (typeof v === 'number' && isNaN(v))
        ? null : Number(v)
    },

    formatPct(v) {
      if (v === null || v === undefined || v === '') return '—'
      const n = Number(v)
      if (isNaN(n)) return '—'
      return `${n.toFixed(1)}%`
    },

    formatDelta(v) {
      if (v === null || v === undefined || v === '') return '—'
      const n = Number(v)
      if (isNaN(n)) return '—'
      return `${n >= 0 ? 'Adelanto: +' : 'Atraso: '}${n.toFixed(1)}%`
    },

    deltaClass(v) {
      if (v === null || v === undefined || v === '') return ''
      const n = Number(v)
      if (isNaN(n)) return ''
      if (n > 0) return 'desv-pos'
      if (n < 0) return 'desv-neg'
      return ''
    },

    // Overlay local controls and drag handlers
    loadOverlayPosition() {
      try {
        const key = `plc_overlay_pos_${this.selectedProject?.id ?? 'global'}`
        const raw = localStorage.getItem(key)
        if (!raw) return
        const pos = JSON.parse(raw)
        if (pos && typeof pos.left === 'number' && typeof pos.top === 'number') {
          this.overlayPosition = { left: pos.left, top: pos.top }
          this.overlayVisible = true
        }
      } catch (e) {
        // ignore
      }
    },

    saveOverlayPosition() {
      try {
        const key = `plc_overlay_pos_${this.selectedProject?.id ?? 'global'}`
        localStorage.setItem(key, JSON.stringify(this.overlayPosition))
      } catch (e) {
				console.log('Error saving overlay position', e)
			}
    },

    openOverlay() {
      this.overlayVisible = true
    },

    closeOverlay() {
      this.overlayVisible = false
    },

    onOverlayPointerDown(e) {
      // only left button
      if (e.button && e.button !== 0) return
      // don't start dragging when interacting with controls inside the overlay
      try {
        if (e.target && e.target.closest && e.target.closest('button,a,input,select,textarea,label,.overlay-close-btn,.overlay-toggle-btn')) return
      } catch (err) {
        // ignore
      }
      const overlayEl = this.$refs.overlayEl
      const wrapper = this.$refs.chartWrapper
      if (!overlayEl || !wrapper) return

      const wrapperRect = wrapper.getBoundingClientRect()
      const overlayRect = overlayEl.getBoundingClientRect()
      let initialLeft = overlayRect.left - wrapperRect.left
      let initialTop = overlayRect.top - wrapperRect.top
      if (this.overlayPosition.left !== null && this.overlayPosition.top !== null) {
        initialLeft = this.overlayPosition.left
        initialTop = this.overlayPosition.top
      }

      this.isDraggingOverlay = true
      this.dragStart = { x: e.clientX, y: e.clientY }
      this.overlayInitialPos = { left: initialLeft, top: initialTop }
      overlayEl.setPointerCapture && overlayEl.setPointerCapture(e.pointerId)
      document.addEventListener('pointermove', this.onOverlayPointerMove)
      document.addEventListener('pointerup', this.onOverlayPointerUp)
    },

    onOverlayPointerMove(e) {
      if (!this.isDraggingOverlay) return
      const dx = e.clientX - this.dragStart.x
      const dy = e.clientY - this.dragStart.y
      const wrapper = this.$refs.chartWrapper
      const overlayEl = this.$refs.overlayEl
      if (!wrapper || !overlayEl) return
      const wrapperRect = wrapper.getBoundingClientRect()
      const overlayRect = overlayEl.getBoundingClientRect()
      const maxLeft = wrapperRect.width - overlayRect.width
      const maxTop = wrapperRect.height - overlayRect.height
      let newLeft = this.overlayInitialPos.left + dx
      let newTop = this.overlayInitialPos.top + dy
      newLeft = Math.max(0, Math.min(maxLeft, newLeft))
      newTop = Math.max(0, Math.min(maxTop, newTop))
      this.overlayPosition.left = Math.round(newLeft)
      this.overlayPosition.top = Math.round(newTop)
    },

    onOverlayPointerUp(e) {
      if (!this.isDraggingOverlay) return
      const overlayEl = this.$refs.overlayEl
      overlayEl && overlayEl.releasePointerCapture && overlayEl.releasePointerCapture(e.pointerId)
      this.isDraggingOverlay = false
      document.removeEventListener('pointermove', this.onOverlayPointerMove)
      document.removeEventListener('pointerup', this.onOverlayPointerUp)
      this.saveOverlayPosition()
    },
  },
}
</script>

<style scoped>
.plc-root {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.plc-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-legend-top {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.15s;
}
.legend-item:hover    { opacity: 0.75; }
.legend-item--hidden  { opacity: 0.35; }

.legend-line {
  display: inline-block;
  width: 20px;
  height: 0;
  border-top-width: 2px;
  flex-shrink: 0;
}
.legend-label { font-size: 10px; color: #6B7280; white-space: nowrap; }
.legend-item--hidden .legend-label { text-decoration: line-through; }

.chart-wrapper { position: relative; min-height: 180px; }
.chart-wrapper canvas { width: 100% !important; }
.chart-label-bottom { text-align: right; font-size: 10px; color: #9CA3AF; margin-top: 4px; flex-shrink: 0; }

/* Overlay styles for metrics (fecha, plan/real/delta) */
.overlay-close-btn{
	position: absolute;
	top: -4px;
	right: -4px;
	z-index: 1003;
	color: #6B7280;
}
.chart-overlay {
  position: absolute;
  bottom: 28%;
  right: 8px;
  z-index: 9;
  background: rgba(255,255,255,0.98);
  padding: 6px 8px;
	padding-top: 15px;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.12);
  font-size: 11px;
  color: #374151;
  max-width: calc(100% - 24px);
  pointer-events: auto;
}
.overlay-toggle-btn{
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1004;
  color: #6B7280;
}

.overlay-hidden {
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
}
.chart-overlay.dragging { cursor: grabbing; }
.chart-overlay { cursor: grab; }
.chart-overlay--compact { display: flex; flex-direction: column; align-items: flex-end; }
.overlay-top { font-weight: 700; font-size: 11px; margin-bottom: 4px; color: #6B7280; }
.overlay-compact { display: flex; gap: 10px; }
.ov-item { display: flex; flex-direction: column; gap: 6px; align-items: flex-start; }
.ov-key { font-weight: 800; font-size: 10px; color: #6B7280; min-width: 24px; text-align: center }
.ov-val { font-size: 11px; color: #111827; font-weight: 700 }
.desv-pos { color: #16A34A; }
.desv-neg { color: #DC2626; }

@media (max-width: 600px) {
  .chart-overlay { display: none; }
}
</style>
